import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { ConversationChannel } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import {
	CHAT_STORE_STATUS,
	type Conversation,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { normalizeRestorableRequestedMode } from './normalizeRestorableRequestedMode';

/**
 * Merges conversation data from local state and API response with network priority.
 * Prioritizes fields from the API (channelsFromAPI) as they represent the latest server state.
 * Falls back to local state (localConversation) for fields missing in the API response.
 */
export const mergeConversationWithNetworkPriority = (
	remoteConversation: ConversationChannel,
	localConversation?: Conversation,
): Conversation => {
	const remoteParticipants = Array.isArray(remoteConversation.participants)
		? remoteConversation.participants
		: undefined;
	const isMentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');

	return {
		id: remoteConversation.id,

		// Prioritize API data if present, otherwise use local or generate new
		name: remoteConversation.name ?? localConversation?.name ?? generateConversationName(),
		timeCreated: remoteConversation.time_created ?? localConversation?.timeCreated,
		lastMessage: remoteConversation.last_message ?? localConversation?.lastMessage ?? undefined,
		lastMessageType:
			remoteConversation.last_message_type ?? localConversation?.lastMessageType ?? [],
		lastMessageTimestamp:
			remoteConversation.last_message_timestamp ?? localConversation?.lastMessageTimestamp,
		lastAgentMessageStatus:
			remoteConversation.last_agent_message_status ?? localConversation?.lastAgentMessageStatus,
		...(remoteConversation.agentSessionState
			? { agentSessionState: remoteConversation.agentSessionState }
			: {}),
		readStatus: remoteConversation.readStatus ?? localConversation?.readStatus,
		isPinned: remoteConversation.isPinned ?? localConversation?.isPinned,
		pinnedAt: remoteConversation.pinnedAt ?? localConversation?.pinnedAt,
		tags: remoteConversation.tags ?? localConversation?.tags,
		// Omission is authoritative: CAP uses it to clear a previously-restorable mode.
		lastRequestedMode: normalizeRestorableRequestedMode(remoteConversation.last_requested_mode),

		// Use local state data if it exists (messages, status are maintained locally)
		messages: localConversation?.messages ?? [],
		status: localConversation?.status ?? CHAT_STORE_STATUS.idle,
		traces: localConversation?.traces ?? [],

		...(localConversation?.sourceAgentId ? { sourceAgentId: localConversation.sourceAgentId } : {}),
		...(localConversation?.sourceId ? { sourceId: localConversation.sourceId } : {}),
		// Front-end-only journey attribution belongs to one exact conversation ID. Preserve it for
		// same-ID refreshes, but never copy it when backend alias hydration changes the ID.
		...(localConversation?.id === remoteConversation.id &&
		localConversation.rovoJourneyId !== undefined
			? { rovoJourneyId: localConversation.rovoJourneyId }
			: {}),
		...(localConversation?.id === remoteConversation.id && localConversation.searchResultsReturn
			? { searchResultsReturn: localConversation.searchResultsReturn }
			: {}),
		...(localConversation?.resultAttributionContext
			? { resultAttributionContext: localConversation.resultAttributionContext }
			: {}),
		// `chatEntryPoint` is front-end-only state — the backend channel never returns it — so it
		// must be carried over from local state or a channel rehydration (init / refocus) would
		// silently drop entry-point attribution mid-conversation.
		...(localConversation?.chatEntryPoint
			? { chatEntryPoint: localConversation.chatEntryPoint }
			: {}),
		// Same reasoning as `chatEntryPoint`: front-end-only, so a rehydration would otherwise
		// drop it and the conversation would stop reporting its cohort mid-flight.
		...(localConversation?.thirdPartyPrompt
			? { thirdPartyPrompt: localConversation.thirdPartyPrompt }
			: {}),
		// Preserve runtime abort controller so Stop button works after channels rehydration (e.g. refocus)
		...(localConversation?.storeAbortController != null
			? { storeAbortController: localConversation.storeAbortController }
			: {}),
		...(localConversation?.rovoSpaceId ? { rovoSpaceId: localConversation.rovoSpaceId } : {}),
		sourceMode: remoteConversation.sourceMode === 'STRICT' ? 'STRICT' : 'GENERAL',
		// `agent` is optional on ConversationChannel, so the server-sourced channel can
		// Preserve the former all-flags-off behavior while retaining the local seed in
		// the experiment variation when the server omits the agent.
		agent: isMentionAgentsExperimentEnabled
			? (remoteConversation.agent ?? localConversation?.agent)
			: remoteConversation.agent,
		scopes: remoteConversation.scopes ?? localConversation?.scopes,
		...(remoteParticipants !== undefined && isMentionAgentsExperimentEnabled
			? { participants: remoteParticipants }
			: {}),
	};
};
